// Pravila naloga za Liste za praćenje (order modal), preslikana 1:1 iz
// insa-trader komponente (provjere, upozorenja, izračuni, nalog za backend).
// Ako se pravila na traderu promijene, uskladiti i ovdje.

export type OrderSide = "Buy" | "Sell";

export interface OrderSecurity {
  SecIndex: number;
  SecStockExchange: number;
  FxRate: number;
  SecFactor: number;
  SecContractSize: number;
}

// Stanje forme naloga - komponenta ga sklapa iz svojih polja.
export interface OrderFormState {
  security: OrderSecurity | null;
  orderType: number;
  validity: string;
  goodTillDate: string;
  amount: number;
  price: number;
  stopPrice: number;
  currentPrice: number;
  tradeTotal: number;
  availableLiquidity: number;
  heldQuantity: number;
  checkCashBalance: boolean;
  allowedOrderTypes: number[];
  isSubmitting: boolean;
  secReservationPercent: number;
}

interface Localizer {
  getLocalizedResource(res: string): string;
}

export const TRANSACTION_TYPE_LABELS: Record<number, string> = {
  1: "Market",
  2: "Limit",
  3: "Limit Fill or Kill",
  4: "Limit FAK",
  5: "Market Fill or Kill",
  6: "Stop Loss",
  7: "Stop Limit",
  8: "On Stop",
  9: "Short Exempt",
  10: "Short Sales",
  11: "Accept Order",
  12: "Market on Close",
  13: "Market Anonymous",
  14: "Limit Anonymous",
  15: "Limit Iceberg",
  16: "Give",
  17: "Take",
  18: "FX Buy",
  19: "FX Sell",
  20: "REPO",
  21: "Reverse REPO",
  22: "Transfer Order",
  23: "Take Correction",
  24: "Block Trade",
  25: "VWAP",
  26: "Pre Market",
  27: "After Market",
  28: "Imm. or Cancel",
};

export function isDomesticStockExchange(secStockExchange: number): boolean {
  return secStockExchange === 12 || secStockExchange === 15;
}

export function transactionTypeLabel(type: number): string {
  return TRANSACTION_TYPE_LABELS[type] ?? `Type ${type}`;
}

export function orderTypeItems(
  allowedTransactionTypes: number[],
): { id: number; label: string }[] {
  return allowedTransactionTypes.map((type) => ({
    id: type,
    label: transactionTypeLabel(type),
  }));
}

export function isImmediateOrderType(orderType: number): boolean {
  return [1, 5, 28].includes(orderType);
}

export function validityOptions(
  localize: Localizer,
  orderType: number,
  isDomestic: boolean,
): { id: string; label: string }[] {
  if (isImmediateOrderType(orderType)) return [];
  const day = {
    id: "Day",
    label: localize.getLocalizedResource("Res3330") || "Dan",
  };
  const gtd = {
    id: "GTD",
    label: localize.getLocalizedResource("Res3331") || "Do datuma",
  };
  const gtc = {
    id: "GTC",
    label: localize.getLocalizedResource("Res3332") || "Do opoziva",
  };
  if (isDomestic) return [day, gtd];
  if (orderType === 26 || orderType === 27) return [day];
  return [day, gtd, gtc];
}

export function validityLabel(
  localize: Localizer,
  orderType: number,
  isDomestic: boolean,
  validity: string,
): string {
  const opt = validityOptions(localize, orderType, isDomestic).find(
    (v) => v.id === validity,
  );
  return opt ? opt.label : validity;
}

export function showStopPriceField(
  orderType: number,
  isDomestic: boolean,
): boolean {
  return orderType === 7 && !isDomestic;
}

export function showDatePicker(orderType: number, validity: string): boolean {
  return !isImmediateOrderType(orderType) && validity === "GTD";
}

export function priceLabel(localize: Localizer, orderType: number): string {
  if (orderType === 7)
    return localize.getLocalizedResource("Res572") || "Limit Cena";
  return localize.getLocalizedResource("Res3534") || "Price";
}

export function fxRateOf(security: OrderSecurity | null): number {
  return security && security.FxRate > 0 ? security.FxRate : 1;
}

export function secFactorOf(security: OrderSecurity | null): number {
  return security && security.SecFactor > 0 ? security.SecFactor : 1;
}

export function secContractSizeOf(security: OrderSecurity | null): number {
  return security && security.SecContractSize > 0
    ? security.SecContractSize
    : 1;
}

// Cijena po kojoj se računa vrijednost: kod Market/odmah naloga trenutna
// cijena, inače unesena.
export function effectivePrice(s: OrderFormState): number {
  return isImmediateOrderType(s.orderType) ? s.currentPrice : s.price;
}

// Vrijednost naloga u valuti hartije.
export function computeTradeTotal(
  price: number,
  amount: number,
  security: OrderSecurity | null,
): number {
  return (
    (price * amount * secContractSizeOf(security)) / secFactorOf(security)
  );
}

export function exceedsLiquidity(s: OrderFormState): boolean {
  // Provjera stanja isključena za partnera - kupovina nikad nije "preko".
  if (!s.checkCashBalance) return false;
  return (
    s.availableLiquidity > 0 &&
    s.tradeTotal * fxRateOf(s.security) > s.availableLiquidity
  );
}

export function noFunds(s: OrderFormState): boolean {
  if (!s.checkCashBalance) return false;
  return s.availableLiquidity <= 0 && s.amount > 0;
}

export function marketOnlyForSell(s: OrderFormState): boolean {
  return (
    !!s.security &&
    !isDomesticStockExchange(s.security.SecStockExchange) &&
    s.orderType === 1
  );
}

export function buyAllowedForType(s: OrderFormState): boolean {
  return s.allowedOrderTypes.length === 0 || s.allowedOrderTypes.includes(0);
}

export function sellAllowedForType(s: OrderFormState): boolean {
  return s.allowedOrderTypes.length === 0 || s.allowedOrderTypes.includes(1);
}

export function canBuy(s: OrderFormState): boolean {
  return (
    s.amount > 0 &&
    !exceedsLiquidity(s) &&
    !noFunds(s) &&
    !s.isSubmitting &&
    !!s.security &&
    !marketOnlyForSell(s) &&
    buyAllowedForType(s) &&
    (isImmediateOrderType(s.orderType) || s.price > 0)
  );
}

export function noHoldings(s: OrderFormState): boolean {
  return !!s.security && s.heldQuantity <= 0;
}

export function exceedsSellAmount(s: OrderFormState): boolean {
  return s.heldQuantity > 0 && s.amount > s.heldQuantity;
}

export function canSell(s: OrderFormState): boolean {
  return (
    s.amount > 0 &&
    !noHoldings(s) &&
    !exceedsSellAmount(s) &&
    !s.isSubmitting &&
    !!s.security &&
    sellAllowedForType(s) &&
    (isImmediateOrderType(s.orderType) || s.price > 0)
  );
}

export function canSubmitSide(s: OrderFormState, side: OrderSide): boolean {
  return side === "Buy" ? canBuy(s) : canSell(s);
}

export function orderPeriod(orderType: number, validity: string): number {
  if (isImmediateOrderType(orderType)) return 1;
  switch (validity) {
    case "Day":
      return 3;
    case "GTC":
      return 6;
    case "GTD":
      return 4;
    default:
      return 3;
  }
}

function toLocalISOString(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

// Nalog za TraderOrderCheck / TraderOrderInsert.
export function buildTraderOrder(
  s: OrderFormState,
  side: OrderSide,
  partnerIndex: number,
): any {
  const immediate = isImmediateOrderType(s.orderType);
  const expiryDate =
    showDatePicker(s.orderType, s.validity) && s.goodTillDate
      ? `${s.goodTillDate}T00:00:00`
      : toLocalISOString(new Date());
  return {
    ...s.security,
    OrderType: side === "Buy" ? 0 : 1,
    TradingType: 1,
    OrderPeriod: orderPeriod(s.orderType, s.validity),
    TransactionType: s.orderType,
    SecurityIndex: s.security?.SecIndex ?? 0,
    PartnerIndex: partnerIndex,
    Quantity: s.amount,
    Price: immediate
      ? side === "Buy" && s.orderType === 1
        ? Math.round(s.currentPrice * s.secReservationPercent * 100) / 100
        : s.currentPrice
      : s.price,
    StopPrice: s.orderType === 7 ? s.stopPrice : null,
    ExpiryDate: expiryDate,
  };
}

function feeLabel(localize: Localizer, key: string): string {
  const map: { [k: string]: string } = {
    Bruto: "Res1147",
    PROVBROK: "Res1148",
    PROVSALD: "Res1149",
    POREZ: "Res1150",
  };
  const resId = map[key];
  return (resId && localize.getLocalizedResource(resId)) || key;
}

// Stavke naknada iz TraderOrderCheck (u valuti hartije, uvijek pozitivne,
// nule se preskaču).
export function feeRows(
  localize: Localizer,
  amounts: { [key: string]: number } | null,
): { key: string; value: number }[] {
  if (!amounts) return [];
  return Object.keys(amounts)
    .filter((k) => k !== "Ukupno")
    .map((k) => ({
      key: feeLabel(localize, k),
      value: Math.abs(amounts[k]),
    }))
    .filter((row) => row.value !== 0);
}

// Neto u valuti hartije (bez FX).
export function checkNet(
  amounts: { [key: string]: number } | null,
): number | null {
  if (!amounts) return null;
  return amounts["Ukupno"] ?? 0;
}

// Ukupno s naknadama u valuti likvidnosti.
export function checkTotal(
  amounts: { [key: string]: number } | null,
  security: OrderSecurity | null,
): number | null {
  if (!amounts) return null;
  return (amounts["Ukupno"] ?? 0) * fxRateOf(security);
}

export function orderResultMessage(
  localize: Localizer,
  result: { success: boolean; description: string } | null,
): string {
  if (!result) return "";
  if (result.description) return result.description;
  return result.success
    ? localize.getLocalizedResource("Res489") || "Order submitted successfully."
    : localize.getLocalizedResource("Res490") || "Order failed.";
}

export function processingText(localize: Localizer): string {
  return localize.getLocalizedResource("Res16") || "Processing...";
}

export function formatDateShort(value: string | Date | null | undefined): string {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "";
  return (kendo as any).toString(d, "dd.MM.yyyy");
}



// WEBPACK FOOTER //
// ./app/insa-components/insa-control-table/control-table-order.logic.ts